import './flow.css';

import { useIonViewWillLeave } from '@ionic/react';
import { cloneDeep, difference, isNil, sample, unionBy } from 'lodash';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDeepCompareEffect } from 'react-use';
import { v4 as uuid } from 'uuid';
import { TeamRole } from '../../../../../api/graphql/generated';
import { useTeamRoleContext } from '../../../../../api/hooks/role.hooks';
import { useCurrentUserProp } from '../../../../../api/user.hooks';
import { useConfirmActionAlert } from '../../../../../components/alert/confirmation-alert';
import { FlipCardProvider } from '../../../../../components/ui/flip-card-context';
import { ContentPanel } from '../../../../../components/ui/panels/content-panel';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { useMachineContext } from '../../../../../pages/machine/machine-context-provider';
import { useIsMounted } from '../../../../../util/hooks';
import { logger } from '../../../../../util/logger';
import { useRegenerateMultipassSection } from '../../../beacon/welding/component/flow-item/multipass/multipass.hooks';
import { useApplicationSettings } from '../../hook/application-settings.hooks';
import { useFlowServices } from '../../hook/flow-service.hooks';
import { useCanRobotTeachWithAlertAsync } from '../../hook/flow-teach.hooks';
import { useShowPlaylistOptions } from '../../hook/flow-ui.hooks';
import { useFlowUnsavedItemHelpers } from '../../hook/flow-unsaved-item.hooks';
import { useLiveFlowExecution } from '../../hook/live-execution.hooks';
import {
  ApplicationDefaultParameterSet,
  Flow,
  FlowItem,
  FlowItemSharedParameterRef,
  FlowItemSharedRef,
  FlowPanel,
  FlowPlaceholder,
  FlowPlaceholderType,
  FlowSaveResult,
  FlowState,
  FlowValidateError,
} from '../../model/flow.types';
import {
  buildFlowSelectedItems,
  generateFlowItemsWithIds,
  getParentIdToChildIds,
  instanceOfFlowItem,
  instanceOfFlowItemSectionParent,
  instanceOfHelper,
  instanceOfPlaceholder,
  processItemsForSave,
} from '../../util/flow-item.util';
import { getParameterValue } from '../../util/flow-parameter.util';
import { scrollToFlowDropZone, scrollToFlowItem } from '../../util/flow.util';
import {
  createUpdatedItemIds,
  detectAffectedMultipassSections,
} from '../../util/multipass-detection.util';
import { EndItemComponent } from '../flow-item/view/end-item';
import { FlowItemAction } from '../flow-item/view/flow-item-base';
import { StartItemComponent } from '../flow-item/view/start-item';
import { FlowErrorToastStatus, FlowErrors } from './flow-errors';
import { FlowRef } from './flow-ref';
import { mergeFlowItemParameters } from './flow.hook';

export interface FlowProps {
  flow: Flow<unknown> & {
    config: { startAtId?: string; stepModeEnabled?: boolean };
  };
  flowRef: FlowRef;
  readOnly?: boolean;
  flowValidationErrors?: FlowValidateError[] | undefined;
  presentActions: () => void;
  showApplicationSettings: () => Promise<boolean>;
}

export enum FlowUpdateMethod {
  LOCAL_ONLY = 'local_only',
  SERVER = 'server',
  SERVER_WITH_OPTIMISTIC_UPDATE = 'server_with_optimistic_update',
}

const FlowComponent = (props: FlowProps): ReactElement => {
  const { flow, flowRef, flowValidationErrors, presentActions } = props;

  const { role } = useTeamRoleContext();
  const isAdmin = useCurrentUserProp<boolean>('admin');
  const readOnly = !isAdmin && (props.readOnly === true || isNil(role));

  const isMounted = useIsMounted();
  const showApplicationSettings = useApplicationSettings(
    props.showApplicationSettings
  );

  const confirmActionAlert = useConfirmActionAlert();

  const { appSlug, flowAnalyticService, flowItemService, flowVersion, system } =
    useFlowServices();
  const { machine } = useMachineContext();
  const isMachineActive = machine?.active;

  const [hasAutoSetTeach, setHasAutoSetTeach] = useState<boolean>(false);

  const executionInfo = useLiveFlowExecution();

  const unsavedItemHelpers = useFlowUnsavedItemHelpers(
    `${appSlug}.part.unsaved_items`
  );

  const settingStartAtRef = useRef<boolean>(false);

  const {
    flowItems,
    flowPanel,
    flowSectionsRef,
    flowState,
    sections,
    selectedItemDetails,
    setSelectedItemIds,
    setFlowState,
    setFlowPanel,
    saveFlow,
    setFlowItems,
    updateFlowConfig,
    componentRegistry: {
      TeachDetail,
      FlowItemEdit,
      FlowItemMultiEdit,
      FlowSections,
    },
  } = flowRef;

  const {
    selectedItemIds,
    selectedItemActualIndex,
    selectedFlowItemInfo,
    selectedFlowItemsPersisted,
  } = selectedItemDetails;

  const { regenerateMultipassSection } = useRegenerateMultipassSection({
    flowItemService,
  });

  useDeepCompareEffect(() => {
    if (isMounted()) {
      setFlowItems(generateFlowItemsWithIds(flow.items));
    }
  }, [flow.items, isMounted, flowVersion, setFlowItems]);

  // store variables as refs so we can access in response to a user action
  // without needing to leverage a hook depenendency that causes unnecessary
  // renders

  const getFlowItems = useRef<() => FlowItem[]>(() => {
    return flowItems.filter(
      (item) => !instanceOfPlaceholder(item)
    ) as FlowItem[];
  });

  useEffect(() => {
    getFlowItems.current = () =>
      flowItems.filter((item) => !instanceOfPlaceholder(item)) as FlowItem[];
  }, [flowItems]);

  const flowPanelRef = useRef<FlowPanel>();
  useEffect(() => {
    flowPanelRef.current = flowPanel;
  }, [flowPanel]);

  const flowStateRef = useRef<FlowState>(FlowState.DEFAULT);
  useEffect(() => {
    flowStateRef.current = flowState;
  }, [flowState]);

  const selectedItemIdsRef = useRef<string[]>([]);
  useEffect(() => {
    selectedItemIdsRef.current = selectedItemIds;
  }, [selectedItemIds]);

  const onSelectItem = useCallback(
    (selectedItemId: string): string[] => {
      // disable selecting an item if we are teaching
      if (flowPanelRef.current === FlowPanel.TEACH) {
        return [];
      }

      const isSelection = !selectedItemIdsRef.current.includes(selectedItemId);

      const flowItems = getFlowItems.current();

      const newItem = flowItems.find((i) => i.id === selectedItemId);

      const isNewItemSection = newItem?.ref === FlowItemSharedRef.SECTION;

      let previousSelectedItems = selectedItemIdsRef.current;

      if (isNewItemSection) {
        previousSelectedItems = isSelection ? [] : [selectedItemId];
      }

      let newSelectedItemIds = buildFlowSelectedItems(
        selectedItemId,
        previousSelectedItems
      );

      if (newSelectedItemIds?.length > 1 && !isNewItemSection) {
        newSelectedItemIds = newSelectedItemIds.filter((id) => {
          const item = flowItems.find((i) => i.id === id);
          return item?.ref !== FlowItemSharedRef.SECTION;
        });
      }

      let panel: FlowPanel | undefined = undefined;

      if (
        newSelectedItemIds.length === 1 &&
        flowStateRef.current === FlowState.DEFAULT
      ) {
        panel = FlowPanel.EDIT;
      } else if (newSelectedItemIds.length > 0) {
        panel = FlowPanel.MULTI_EDIT;
      } else {
        panel = undefined;
      }

      setSelectedItemIds(newSelectedItemIds, panel);

      if (isSelection) {
        flowAnalyticService.trackSelectItems({
          method: 'item',
          count: 1,
          totalSelected: selectedItemIdsRef.current.length + 1,
          flowLength: flowItems.length,
        });
      } else {
        flowAnalyticService.trackUnselectItems({
          method: 'item',
          count: 1,
          totalSelected: selectedItemIdsRef.current.length - 1,
          flowLength: flowItems.length,
        });
      }

      return newSelectedItemIds;
    },
    [setSelectedItemIds, flowAnalyticService]
  );

  const unselectAll = useCallback(() => {
    if (isMounted()) {
      setSelectedItemIds([]);
    }
  }, [isMounted, setSelectedItemIds]);

  const deleteItems = useCallback(
    async (indices: number[]) => {
      if (!indices.length) {
        return;
      }

      logger.info('deleting items from flow', { indices });

      const items = flowItems.filter(
        (item, idx) => !instanceOfPlaceholder(item) && !indices.includes(idx)
      ) as FlowItem[];

      const updatedItems = updateParentEndItemIds(
        flowItems.filter(instanceOfFlowItem),
        items
      );

      await saveFlow(updatedItems, {
        updateMethod: FlowUpdateMethod.SERVER_WITH_OPTIMISTIC_UPDATE,
        saveAsDelta: true,
      });

      logger.info('finished deleting items from flow', {
        numOfItems: indices.length,
      });
    },
    [flowItems, saveFlow]
  );

  const onDeleteItem = useCallback(
    (onClose: (cb: () => void) => void) => {
      return async (itemsForDelete: FlowItem[]) => {
        onClose(async () => {
          const items = flowItems.filter(
            (item) => !instanceOfPlaceholder(item)
          ) as FlowItem[];

          const indices: number[] = itemsForDelete.map((itemForDelete) => {
            return items.findIndex((i) => i.id === itemForDelete.id);
          });

          if (indices?.length > 0) {
            unselectAll();
            await deleteItems(indices);
          }
        });
      };
    },
    [deleteItems, flowItems, unselectAll]
  );

  const performMultiEdit = async (
    item: Partial<FlowItem>,
    updateFlowSettings = false
  ): Promise<void> => {
    const defaultItem = flowItemService.getDefaultItemForRef(
      // the default item ref could be different by application. examples could be
      // FlowItemRef.WELD or FlowItemRef.CUT
      flowItemService.getDefaultItemRef()
    );

    const filtered = flowItems
      .filter((i) => {
        const id = (i as FlowItem).id;
        if (id) {
          return selectedItemIds.includes(id);
        } else {
          return false;
        }
      })
      .filter((i) => (i as FlowItem).ref === item.ref) as FlowItem[];

    if (filtered.length > 0) {
      const updatedItems: FlowItem[] = [];

      for (const existingItem of filtered) {
        const { parameters: defaultParams = [] } = defaultItem;
        const { parameters: existingParams = [] } = existingItem;
        const { parameters: itemParams = [] } = item;

        // The order of the arrays are important since the last array will have
        // the highest priority. We want to apply the default as a backup,
        // then existing parameters, and finally the updates which
        // should overwrite them all.
        const mergedParams = mergeFlowItemParameters([
          // The default parameters are important in the case that the process
          // is changed (or other ref) which causes additional refs to be
          // required. Likely the form will catch the error first, but this is
          // a safety net.
          defaultParams,
          existingParams,
          itemParams,
        ]);

        const mergedItem: FlowItem = {
          ...existingItem,
          parameters: mergedParams,
        };

        updatedItems.push(mergedItem);
      }

      let defaultParameterSet: ApplicationDefaultParameterSet | undefined =
        undefined;
      if (updateFlowSettings && item.parameters && flow.defaultParameterSet) {
        const parameters = unionBy(
          [],
          item.parameters,
          flow.defaultParameterSet.data.parameters,
          'ref'
        );
        defaultParameterSet = {
          ...flow.defaultParameterSet,
          data: { parameters },
        };
      }

      await onUpdateItems(updatedItems, {
        defaultParameterSet,
        saveAsDelta: true,
      });
    }
    logger.info('completed multi edit updates', {
      numOfItems: filtered.length,
    });
  };

  const deleteTemp = useCallback(
    (indices: number[]) => {
      const items = flowItems.filter((_, idx) => !indices.includes(idx));
      saveFlow(items, {
        updateMethod: FlowUpdateMethod.LOCAL_ONLY,
      });
    },
    [flowItems, saveFlow]
  );

  const updateFlowStartAt = useCallback(
    async (
      id: string,
      longPress: boolean,
      withStepMode?: boolean
    ): Promise<void> => {
      if (settingStartAtRef.current === false) {
        settingStartAtRef.current = true;

        const startAtId = id === flow.config.startAtId ? undefined : id;

        if (withStepMode !== undefined && withStepMode !== false) {
          await updateFlowConfig({
            startAtId,
            stepModeEnabled: withStepMode ?? false,
          });
          flowAnalyticService.trackEnableStepMode('startFromHere');
        } else {
          await updateFlowConfig({
            startAtId,
          });

          flowAnalyticService.trackStartFromHere(longPress);
        }

        settingStartAtRef.current = false;
      }
    },
    [flow.config.startAtId, updateFlowConfig, flowAnalyticService]
  );

  const discard = useCallback(() => {
    const items = flowItems.filter(
      (item) => !instanceOfHelper(item) && item.id
    ) as FlowItem[];

    unsavedItemHelpers.clear();

    saveFlow(items, {
      updateMethod: FlowUpdateMethod.LOCAL_ONLY,
    });
  }, [flowItems, saveFlow, unsavedItemHelpers]);

  const getActions = useCallback(
    (index: number): FlowItemAction[] | undefined => {
      const item = flowItems[index] as FlowItem;
      if (!item.id) {
        return [
          {
            name: `${getLocalizedText('common:delete')}`,
            action: () => deleteTemp([index]),
          },
        ];
      }
    },
    [deleteTemp, flowItems]
  );

  useIonViewWillLeave(() => {
    setFlowState(FlowState.DEFAULT);
    setFlowPanel(undefined);
  }, []);

  const { error: teachModeError, present: presentTeachModeError } =
    useCanRobotTeachWithAlertAsync();

  const onAddItemsAfter = useCallback(
    async (index?: number) => {
      const valid = await showApplicationSettings();

      if (!valid) {
        logger.info('flow not valid for teaching');
        return;
      }

      if (teachModeError) {
        presentTeachModeError();
        return;
      }

      const pos = index !== undefined ? index + 1 : 0;

      const start: FlowPlaceholder = {
        identifier: 'placeholder',
        type: FlowPlaceholderType.START,
      };

      const middle: FlowPlaceholder = {
        identifier: 'placeholder',
        type: FlowPlaceholderType.MIDDLE,
      };

      const drop: FlowPlaceholder = {
        identifier: 'placeholder',
        type: FlowPlaceholderType.DROP,
      };

      await unsavedItemHelpers.present((cachedItems?: FlowItem[]) => {
        const items = [
          ...flowItems.slice(0, pos),
          start,
          ...(cachedItems ?? [middle]),
          drop,
          ...flowItems.slice(pos),
        ];

        saveFlow(items, {
          updateMethod: FlowUpdateMethod.LOCAL_ONLY,
        });
        setFlowState(FlowState.TEACH);
        setFlowPanel(FlowPanel.TEACH);
        requestAnimationFrame(scrollToFlowDropZone);
      });
    },
    [
      flowItems,
      presentTeachModeError,
      saveFlow,
      setFlowPanel,
      setFlowState,
      showApplicationSettings,
      teachModeError,
      unsavedItemHelpers,
    ]
  );

  useDeepCompareEffect(() => {
    if (
      flowState === FlowState.DEFAULT &&
      flow.items.length === 0 &&
      !hasAutoSetTeach &&
      isMachineActive &&
      !readOnly
    ) {
      onAddItemsAfter(0);
      setHasAutoSetTeach(true);
    }
  }, [
    flowState,
    flow,
    hasAutoSetTeach,
    onAddItemsAfter,
    isMachineActive,
    readOnly,
  ]);

  const onAddToDropZone = async (flowItemsIn: FlowItem[]): Promise<void> => {
    const filteredItems = flowItems.filter((item) => {
      if (
        instanceOfPlaceholder(item) &&
        item.type === FlowPlaceholderType.MIDDLE
      ) {
        return false;
      }
      return true;
    });

    const dropIndex = filteredItems.findIndex((item) => {
      return (
        instanceOfPlaceholder(item) && item.type === FlowPlaceholderType.DROP
      );
    });

    if (dropIndex === -1) {
      console.log(filteredItems);
      console.warn('placeholder not found');
      return;
    }

    const items = [
      ...filteredItems.slice(0, dropIndex),
      ...flowItemsIn,
      ...filteredItems.slice(dropIndex),
    ];

    const unsavedItems = items.filter(
      (item) => !instanceOfHelper(item) && item.id === undefined
    ) as FlowItem[];

    unsavedItemHelpers.setItems(unsavedItems);

    await saveFlow(items, {
      updateMethod: FlowUpdateMethod.LOCAL_ONLY,
    });
    requestAnimationFrame(scrollToFlowDropZone);
  };

  const onAddSectionAtIndex = async (index: number): Promise<void> => {
    let items = getFlowItems.current();
    const section = flowItemService.getDefaultItemForRef(
      FlowItemSharedRef.SECTION
    );

    section.id = uuid();
    section.name = getLocalizedText('common:section');

    // twitter colors
    // http://casesandberg.github.io/react-color/
    const colors = [
      '#FF6900',
      '#FCB900',
      '#7BDCB5',
      '#00D084',
      '#8ED1FC',
      '#0693E3',
      '#ABB8C3',
      '#EB144C',
      '#F78DA7',
      '#9900EF',
    ];

    const existingColors = items
      .filter((i) => i.ref === FlowItemSharedRef.SECTION)
      .map(
        (i) =>
          getParameterValue<string>(
            i,
            FlowItemSharedParameterRef.SECTION_COLOR
          )?.toUpperCase()
      )
      .filter((c) => c !== undefined);

    const unusedColors = difference(colors, existingColors);

    const color = unusedColors[0] ?? sample(colors);

    flowItemService.setParameterValue(
      section,
      FlowItemSharedParameterRef.SECTION_COLOR,
      color
    );

    items.splice(index, 0, section);

    items = generateFlowItemsWithIds(items);

    await saveFlow(items, {
      updateMethod: FlowUpdateMethod.SERVER_WITH_OPTIMISTIC_UPDATE,
      saveAsDelta: true,
    });
  };

  // Use the shared utility function for multipass detection
  const onUpdateItems = useCallback(
    async (
      items: FlowItem[],
      options?: {
        defaultParameterSet?: ApplicationDefaultParameterSet;
        saveAsDelta?: boolean;
      }
    ) => {
      const clonedItems = cloneDeep(flowItems as FlowItem[]);
      const updatedItemIds = createUpdatedItemIds(items);
      const updateBaseValues =
        isAdmin ||
        role?.type === TeamRole.MANAGER ||
        role?.type === TeamRole.EDITOR;

      for (const idx in items) {
        const updatedItem = items[idx];
        const existingIdx = clonedItems.findIndex(
          (fi) => fi.id === updatedItem.id
        );

        if (existingIdx > -1) {
          let updatedParameters = updatedItem.parameters;
          if (updateBaseValues) {
            updatedParameters = updatedParameters.map((param) => {
              return {
                ...param,
                baseValue: param.value,
              };
            });
          }

          clonedItems[existingIdx] = {
            ...updatedItem,
            parameters: updatedParameters,
          };
        }
      }

      // Check for multipass sections that need their originalItems updated
      const { updatedItems, multipassSectionsToUpdate } =
        detectAffectedMultipassSections(clonedItems, updatedItemIds);

      // If there are multipass sections to update, prompt user before saving
      if (multipassSectionsToUpdate.length > 0) {
        logger.info('Detected multipass sections that need regeneration', {
          updatedItemCount: items.length,
          multipassSectionsUpdated: multipassSectionsToUpdate.length,
          multipassSectionIds: multipassSectionsToUpdate.map(
            (section) => section.id
          ),
        });

        // Prompt user to regenerate multipass sections before saving
        const promptForMultipassRegeneration = confirmActionAlert({
          header:
            getLocalizedText('welding:multipass_update') ?? 'Multipass Update',
          message:
            getLocalizedText('welding:multipass_update_message', {
              count: multipassSectionsToUpdate.length,
              plural: multipassSectionsToUpdate.length > 1 ? 's' : '',
            }) ??
            `The root pass has changed for ${
              multipassSectionsToUpdate.length
            } multipass section${
              multipassSectionsToUpdate.length > 1 ? 's' : ''
            }. Do you want to apply the changes to all passes?`,
          confirmText:
            getLocalizedText('welding:multipass_apply_changes') ??
            'Apply Changes',
          cancelText:
            getLocalizedText('welding:multipass_keep_original') ??
            'Keep Original',
          confirmationAction: async () => {
            // Regenerate each affected multipass section, then save once
            let finalUpdatedItems = updatedItems;
            let currentFlowItems = clonedItems;

            for (const multipassSection of multipassSectionsToUpdate) {
              finalUpdatedItems = await regenerateMultipassSection(
                currentFlowItems,
                multipassSection,
                finalUpdatedItems
              );
              // Update currentFlowItems for the next iteration
              currentFlowItems = finalUpdatedItems;
            }

            // Save once with all changes including regenerated multipass sections
            await saveFlow(finalUpdatedItems, {
              updateMethod: FlowUpdateMethod.SERVER,
              applicationDefaultParameterSet: options?.defaultParameterSet,
              saveAsDelta: options?.saveAsDelta,
            });
          },
          onCancel: async () => {
            // Save just the updated items without regenerating multipass sections
            await saveFlow(updatedItems, {
              updateMethod: FlowUpdateMethod.SERVER,
              applicationDefaultParameterSet: options?.defaultParameterSet,
              saveAsDelta: options?.saveAsDelta,
            });
          },
        });

        // Show the confirmation dialog
        promptForMultipassRegeneration();
      } else {
        // No multipass sections affected, save normally
        await saveFlow(updatedItems, {
          updateMethod: FlowUpdateMethod.SERVER,
          applicationDefaultParameterSet: options?.defaultParameterSet,
          saveAsDelta: options?.saveAsDelta,
        });
      }
    },
    [
      flowItems,
      isAdmin,
      role?.type,
      saveFlow,
      confirmActionAlert,
      regenerateMultipassSection,
    ]
  );

  const onTeachSave = useCallback(async (): Promise<
    FlowSaveResult | undefined
  > => {
    let result: FlowSaveResult | undefined;
    const items = getFlowItems.current();
    const firstNewItemIndex = items.findIndex((item) => item.id === undefined);
    if (firstNewItemIndex > -1) {
      const processedItems = processItemsForSave(items);

      result = await saveFlow(processedItems, {
        updateMethod: FlowUpdateMethod.SERVER,
        saveAsDelta: true,
      });
      if (result) {
        unsavedItemHelpers.clear();

        const firstNewItemId = processedItems[firstNewItemIndex].id;
        if (firstNewItemId) {
          scrollToFlowItem(firstNewItemId);
        }
        setFlowState(FlowState.DEFAULT);
      }
    }
    return result;
  }, [saveFlow, setFlowState, unsavedItemHelpers]);

  const showPlaylistOptions = useShowPlaylistOptions(
    flowItems.length > 0,
    presentActions
  );

  const hasUnsavedItems =
    flowItems.filter((item) => !instanceOfHelper(item) && item.id === undefined)
      .length > 0;

  const canAddReorderItem = flowState === FlowState.EDIT;

  const shouldShowExecutionStatus = useMemo<boolean>(() => {
    return flowState === FlowState.DEFAULT;
  }, [flowState]);

  useEffect(() => {
    const localItems = getFlowItems.current();

    if (
      shouldShowExecutionStatus &&
      executionInfo &&
      executionInfo.index > -1 &&
      executionInfo.index < localItems.length
    ) {
      const item = localItems[executionInfo.index];
      if (item.id) {
        scrollToFlowItem(item.id);
      }
    }
  }, [shouldShowExecutionStatus, executionInfo, flowState]);

  const startItemIndex = useMemo(() => {
    if (flow.config.startAtId === undefined) {
      return -1;
    }

    return flowItems.findIndex((item) => {
      return !instanceOfHelper(item) && item.id === flow.config.startAtId;
    });
  }, [flow.config.startAtId, flowItems]);

  const flowItemTextArr = useMemo(() => {
    return flowItemService.buildFlowItemTextArr(flowItems);
  }, [flowItemService, flowItems]);

  const [sidePanelOpen, setSidePanelOpen] = useState(false);

  const flowItemClasses = useMemo(() => {
    return sidePanelOpen ? ['contrained-side-panel-width'] : undefined;
  }, [sidePanelOpen]);

  return (
    <>
      <FlowErrors
        flowValidationErrors={flowValidationErrors ?? []}
        jumpToItem={scrollToFlowItem}
        status={FlowErrorToastStatus.DANGER}
      />
      <div className="flow-container">
        <StartItemComponent
          onAddItemsAfter={onAddItemsAfter}
          canAddItem={canAddReorderItem}
        />
        <FlowSections
          ref={flowSectionsRef}
          canAddReorderItem={canAddReorderItem}
          flow={flow}
          flowItemTextArr={flowItemTextArr}
          flowItemClasses={flowItemClasses}
          flowState={flowState}
          getActions={getActions}
          getFlowItemsRef={getFlowItems}
          machine={machine}
          onAddItemsAfter={onAddItemsAfter}
          onSelectItem={onSelectItem}
          saveFlow={saveFlow}
          sections={sections}
          selectedItemIds={selectedItemIds}
          shouldShowExecutionStatus={shouldShowExecutionStatus}
          startItemIndex={startItemIndex}
          updateFlowStartAt={updateFlowStartAt}
          executionInfo={executionInfo}
          flowValidationErrors={flowValidationErrors}
          onAddSectionAtIndex={onAddSectionAtIndex}
          system={system}
        />
        <EndItemComponent />
        {/*
          We can do something better here.  The issue is when the drop zone is near the bottom of
          the container, we can't scroll it up enough to show above the panel.
        */}
        {flowPanel === FlowPanel.TEACH && <div style={{ height: '60vh' }} />}
      </div>
      <ContentPanel
        show={flowPanel === FlowPanel.TEACH}
        onPanelChange={(open, panel) => {
          if (panel === 'side') {
            setSidePanelOpen(open);
          }
        }}
        onDidDismiss={() => {
          discard();
          if (flowPanel === FlowPanel.TEACH) {
            setFlowPanel(undefined);
            setFlowState(FlowState.DEFAULT);
          }
        }}
        disableClose={true}
        canScrollBackdrop={true}
      >
        {({ dismiss }) => (
          <TeachDetail
            initialItemRef={flowItemService.getDefaultItemRef()}
            onAdd={onAddToDropZone}
            onDiscard={dismiss}
            hasUnsavedItems={hasUnsavedItems}
            onSave={async () => {
              const result = await onTeachSave();
              if (result) {
                setFlowPanel(undefined);
                showPlaylistOptions();
              }
            }}
            flow={{ ...flow, items: getFlowItems.current() }}
            onTeachError={async () => {
              if (hasUnsavedItems) {
                await onTeachSave();
              }
            }}
          />
        )}
      </ContentPanel>
      <ContentPanel
        show={flowPanel === FlowPanel.EDIT}
        onDidDismiss={() => {
          if (flowPanel === FlowPanel.EDIT) {
            setFlowPanel(undefined);
            setSelectedItemIds([]);
          }
        }}
        bottomPanelProps={{ enabledBottomBreak: true }}
        canScrollBackdrop={true}
      >
        {({ onClose }) => (
          <FlipCardProvider>
            <FlowItemEdit
              item={selectedFlowItemsPersisted[0]}
              itemNamePrefix={flowItemTextArr[selectedItemActualIndex]?.index}
              onUpdateItem={(item) =>
                onUpdateItems([item], { saveAsDelta: true })
              }
              onDeleteItem={onDeleteItem(onClose)}
              readOnly={readOnly}
              system={system}
              setStartAtId={async (id: string, enableStepMode?: boolean) => {
                return updateFlowStartAt(id, false, enableStepMode);
              }}
              startAtId={flow.config.startAtId}
              onClosed={() => {
                onClose(() => {
                  unselectAll();
                });
              }}
              executionInfo={
                executionInfo?.index === selectedItemActualIndex
                  ? executionInfo
                  : undefined
              }
            />
          </FlipCardProvider>
        )}
      </ContentPanel>
      <ContentPanel
        show={flowPanel === FlowPanel.MULTI_EDIT}
        onDidDismiss={() => {
          if (flowPanel === FlowPanel.MULTI_EDIT) {
            setFlowPanel(undefined);
            unselectAll();
            setFlowState(FlowState.DEFAULT);
          }
        }}
        bottomPanelProps={{ enabledBottomBreak: true }}
        canScrollBackdrop={true}
      >
        {({ dismiss, onClose }) => (
          <FlipCardProvider>
            <FlowItemMultiEdit
              items={selectedFlowItemsPersisted}
              onClosed={dismiss}
              onUpdateItem={async (item, updateFlowConfig) => {
                onClose(() => {
                  performMultiEdit(item, updateFlowConfig);
                });
              }}
              readOnly={readOnly}
              system={system}
              onDeleteItems={async (itemIndicesForDelete: number[]) => {
                onClose(() => {
                  deleteItems(itemIndicesForDelete);
                });
              }}
              selectedItemInfo={selectedFlowItemInfo}
            />
          </FlipCardProvider>
        )}
      </ContentPanel>
    </>
  );
};

const updateParentEndItemIds = (
  existingItems: FlowItem[],
  newItems: FlowItem[]
): FlowItem[] => {
  const parentToChildrenIds = getParentIdToChildIds(existingItems);
  const modifiedItems = cloneDeep(newItems);

  for (let i = 0; i < modifiedItems.length; i++) {
    const item = modifiedItems[i];
    if (
      item.id &&
      parentToChildrenIds[item.id] &&
      instanceOfFlowItemSectionParent(item) // leverage type safety
    ) {
      // if there are no more items, then we need to remove the end item id
      if (i === modifiedItems.length - 1) {
        item.attributes.endItemId = undefined;
        continue;
      }

      // find the index of the last section child
      const outerBound = modifiedItems.slice(i + 1).findIndex((nextItem) => {
        return (
          nextItem?.id && !parentToChildrenIds[item.id].includes(nextItem.id)
        );
      });

      if (outerBound === -1) {
        // the last item is a child of the section
        const lastItem = modifiedItems[modifiedItems.length - 1];

        // the parent's endItemId should always be the last child
        item.attributes.endItemId = lastItem.id;

        i += parentToChildrenIds[item.id].length;
      } else {
        // the parent's endItemId should be the outerBound item
        item.attributes.endItemId = modifiedItems[i + outerBound].id;
        i += outerBound;
      }
    }
  }

  return modifiedItems;
};

export { FlowComponent };
